/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useEffect, useMemo, useState} from 'react'
import {FormattedMessage, useIntl} from 'react-intl'
import PropTypes from 'prop-types'
import {Progress} from '@chakra-ui/react'
import {
    Box,
    Flex,
    Text,
    useMultiStyleConfig,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {calculateCountMap, updateCountMap, getMaterialGroups} from '../../../utils/utils'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useHandleEmployeeBalance} from '../../../custom-hooks/use-handle-employee-balance'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'

const EmployeeCategorySalesBalance = ({employeeOrderSummary, employeeBalancePrefData}) => {
    const materialGroups = useMemo(() => {
        if (!employeeBalancePrefData?.products) return []

        const countMap = calculateCountMap(employeeBalancePrefData.products)

        if (employeeOrderSummary?.response?.records) {
            updateCountMap(countMap, employeeOrderSummary?.response?.records)
        }

        return getMaterialGroups(employeeBalancePrefData.products, countMap)
    }, [employeeOrderSummary, employeeBalancePrefData])

    return (
        <Flex direction="column" gap={2}>
            {materialGroups.length > 0 &&
                materialGroups.map(({materialGroup, displayValue}, index) => (
                    <Box key={index}>
                        {materialGroup}: {displayValue}
                    </Box>
                ))}
        </Flex>
    )
}

EmployeeCategorySalesBalance.propTypes = {
    employeeOrderSummary: PropTypes.object,
    employeeBalancePrefData: PropTypes.object,
}

const EmployeeBalance = () => {
    const styles = useMultiStyleConfig('DashboardComponents')
    const [isLoading, setIsLoading] = useState(true)
    const {currency} = useCurrency()
    const intl = useIntl()

    const {data: customer} = useCurrentCustomer()
    const {handleEmployeeBalance, employeeBalancePrefData, employeeError, employeeData} =
        useHandleEmployeeBalance()
    const [spent, setSpent] = useState(0)
    const [total, setTotal] = useState(1500)
    const [percentage, setPercentage] = useState(0)
    /**
     * Fetch employee balance from OMS
     */
    useEffect(() => {
        const fetchEmployeeBalance = async () => {
            const result = await handleEmployeeBalance()
            if (result) {
                setSpent(result.totalAmountWithTax || 0)
                setTotal(parseFloat(employeeBalancePrefData?.balance || 0) || 1500)
                setPercentage(
                    ((result.totalAmountWithTax || 0) /
                        (employeeBalancePrefData?.balance || 1500)) *
                        100,
                )
                setIsLoading(false)
            }
        }

        fetchEmployeeBalance()
    }, [customer.c_gigyaId && customer.email])

    const listPriceText = Number.isInteger(total)
        ? intl.formatNumber(total, {
              style: 'currency',
              currency,
              minimumFractionDigits: 0, // No decimals for integers
              maximumFractionDigits: 0,
          })
        : intl.formatNumber(total, {
              style: 'currency',
              currency,
          })

    const spentPriceText = Number.isInteger(spent)
        ? intl.formatNumber(spent, {
              style: 'currency',
              currency,
              minimumFractionDigits: 0, // No decimals for integers
              maximumFractionDigits: 0,
          })
        : intl.formatNumber(spent, {
              style: 'currency',
              currency,
          })

    return (
        <Box data-testid="employee-sales-component" {...styles.employeeContainer}>
            {isLoading && <LoadingSpinner />}
            {/* show only if we have data from OMS and custom preference */}
            {!isLoading && (
                <Flex flexDir={'column'} gap={6}>
                    <Text as="h3" tabIndex="0" {...styles.employeeTitle}>
                        <FormattedMessage
                            defaultMessage="Employee sales details"
                            id="account_detail.title.account_employee_sales"
                        />
                    </Text>
                    <Text {...styles.employeeDescription}>
                        {intl.formatMessage({
                            id: 'account_detail.description.account_employee_sales',
                            defaultMessage:
                                "As an employee of the De'Longhi Group, you have a maximum amount available for purchasing discounted products. Below you will find your current balance",
                        })}
                    </Text>
                    {employeeData && employeeBalancePrefData && (
                        <Flex flexDir={'column'} gap={6}>
                            <Box>
                                <Text fontWeight="bold">
                                    {spentPriceText} / {listPriceText}{' '}
                                    {intl.formatMessage({
                                        id: 'account_detail.account_employee_total',
                                        defaultMessage: 'Spent',
                                    })}
                                </Text>
                                <Progress
                                    value={percentage}
                                    size="md"
                                    sx={{
                                        '& div[role="progressbar"]': {
                                            backgroundColor: 'black',
                                        },
                                    }}
                                    borderRadius="md"
                                    maxW={'259px'}
                                />
                            </Box>
                            <Box>
                                {''}
                                <EmployeeCategorySalesBalance
                                    employeeOrderSummary={employeeData}
                                    employeeBalancePrefData={employeeBalancePrefData}
                                />
                            </Box>
                        </Flex>
                    )}
                    {employeeError && (
                        <Flex justify="center" mb={4}>
                            <Text color="red.500" fontWeight="bold">
                                {employeeError}
                            </Text>
                        </Flex>
                    )}
                </Flex>
            )}
        </Box>
    )
}

EmployeeBalance.getTemplateName = () => 'employee-balance'

EmployeeBalance.propTypes = {
    match: PropTypes.object,
}

export default EmployeeBalance
